import type { AdminRole } from '@/types/marketplace'

/**
 * Platform-level capability checks.
 *
 * These live in their own module, with NO server-only imports, because both
 * sides of the console need the exact same answer:
 *
 *   - the server guards (`requireAdminApi`, `requireAdminPage` and every
 *     platform page loader) use them to ADMIT or REFUSE a request;
 *   - the console navigation uses them to SHOW or HIDE the entry that leads
 *     there.
 *
 * Keeping one definition is the point. Hiding a link is UX only and is never
 * the protection — the route behind it still re-checks on every request — but a
 * second, hand-copied role list in a component is how the two silently drift
 * apart. `@/lib/auth/session` re-exports these, so existing server imports are
 * unchanged.
 *
 * The role itself always comes from the database, re-read per request by
 * `getSession`, never from a request body, query string or cookie payload.
 */

/** Roles allowed to change money, pricing, categories and platform settings. */
export const canManageSettings = (role: AdminRole): boolean => role === 'owner' || role === 'admin'

/** Roles allowed to moderate listings, sellers and reports. */
export const canModerate = (role: AdminRole): boolean => role === 'owner' || role === 'admin' || role === 'moderator'

/**
 * Access to the PLATFORM surface: the marketplace directory and the community
 * application queue at `/admin/communities`.
 *
 * Deliberately the same predicate as `canManageSettings`, because approving,
 * rejecting and suspending a marketplace is a platform settings decision — this
 * is a name for the capability, not a second rule. Community-level authority
 * (`community_memberships`, checked through `@/lib/community/authorization`)
 * grants nothing here: administering one marketplace never admits anyone to the
 * console that governs all of them.
 *
 * Every gate on that surface asks THIS function: the two page loaders, both API
 * routes (through `requireAdminApi({ platform: true })`), the `platformAdmin`
 * flag on a community context, and the sidebar group that links there. Widening
 * platform access later means editing this line and nothing else.
 */
export const canAdministerPlatform = (role: AdminRole): boolean => canManageSettings(role)

/**
 * The same question, asked of a role that has not been narrowed yet.
 *
 * `@/lib/community/context` derives platform authority from a raw `sellers.role`
 * string straight out of the database, so it cannot use the typed check above
 * without casting. Narrowing here keeps that derivation on the one rule instead
 * of re-listing the roles at the call site. Anything unrecognised is refused.
 */
export const isPlatformAdminRole = (role: string | null | undefined): boolean =>
    role === 'owner' || role === 'admin' || role === 'moderator'
        ? canAdministerPlatform(role)
        : false
